import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from 'components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { Icon, IconName } from '../menu-icon/menu-icon';
import { rightIconWithModalProps, SidebarMenuItemProps } from 'models/sidebar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuContent,
} from '../../../components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';

export const SidebarMenuItemComponent: React.FC<SidebarMenuItemProps> = ({
  item,
  showText,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const { pathname } = useLocation();
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const isParentActive =
    hasChildren && item.children?.some((child) => pathname.startsWith(child.path));
  const isActive = pathname.startsWith(item.path) || isParentActive;

  useEffect(() => {
    if (isParentActive && !isOpen) {
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isParentActive]);

  const strokeWidth = 2.2;

  const rightIconWithModal = ({ iconName, options }: rightIconWithModalProps) => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="ml-1 p-1 rounded-md hover:bg-muted-foreground/10">
            <Icon
              name={iconName}
              size={18}
              strokeWidth={strokeWidth}
              className={`${isActive ? 'text-primary' : 'text-high-emphasis'}`}
            />
          </button>
        </DropdownMenuTrigger>
        {options && options.length > 0 && (
          <DropdownMenuContent side="right" align="end" className="w-48">
            {options.map((option) => (
              <DropdownMenuItem
                key={option.id}
                onClick={option.callback}
                className="flex items-center gap-2"
              >
                <Icon
                  name={option.icon as IconName}
                  size={16}
                  strokeWidth={strokeWidth}
                  className={`${isActive ? 'text-primary' : 'text-high-emphasis'}`}
                />
                <span>{option.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        )}
      </DropdownMenu>
    );
  };

  const renderIcon = (iconName: IconName | undefined) => {
    if (!iconName) return null;

    return (
      <div className="flex items-center justify-center w-6 h-6">
        <Icon
          name={iconName}
          size={16}
          strokeWidth={strokeWidth}
          className={`${isActive || isHovered ? 'text-text-dark' : 'text-sidebar-foreground'}`}
        />
      </div>
    );
  };

  const renderImageIcon = (image: string | undefined) => {
    if (!image) return null;

    return (
      <div className="flex items-center justify-center w-6 h-6 p-1 rounded-full bg-light-beige">
        <img
          src={image}
          alt="icon"
          className="w-4 h-4 object-contain"
        />
      </div>
    );
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  if (hasChildren) {
    return (
      <Collapsible className="group/collapsible" open={isOpen} onOpenChange={setIsOpen}>
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton onClick={handleClick}>
              <div className="flex items-center justify-start w-full">
                {renderIcon(item.icon as IconName)}
                <span
                  className={`ml-3 truncate ${!showText && 'hidden'} ${isActive ? 'text-primary' : 'text-high-emphasis'} text-base`}
                >
                  {t(item.name)}
                </span>
                {showText && (
                  <ChevronRight
                    strokeWidth={strokeWidth}
                    className={`${isActive ? 'text-primary' : 'text-high-emphasis'} ml-auto h-5 w-5 transition-transform group-data-[state=open]/collapsible:rotate-90`}
                  />
                )}
              </div>
            </SidebarMenuButton>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <SidebarMenuSub>
              {item.children?.map((child) => {
                // Check if current path starts with child path for nested routes
                const isChildActive = pathname.startsWith(child.path);
                return (
                  <SidebarMenuSubItem key={child.id}>
                    <SidebarMenuSubButton asChild className={isChildActive ? 'bg-surface' : ''}>
                      <Link
                        to={child.path}
                        className="flex items-center w-full"
                        onClick={handleClick}
                      >
                        {renderIcon(child.icon as IconName)}
                        <span
                          className={`ml-3 truncate ${!showText && 'hidden'} ${isChildActive ? 'text-primary' : 'text-high-emphasis'} text-base`}
                        >
                          {child.name}
                        </span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  }

  return (
    <SidebarMenuItem
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <SidebarMenuButton asChild className={`h-10 hover:bg-custom-beige rounded-[8px] ${isActive ? 'bg-custom-beige !p-0' : '!p-0'}`}>
        <div className="flex items-center justify-between w-full">
          <div className="w-[80%]">
            <Link to={item.path} className="block w-full h-full" onClick={handleClick}>
              <div className="flex items-center justify-start w-full gap-3 py-2 px-2">
                {item.imageIcon ? renderImageIcon(item.imageIcon) : renderIcon(item.icon as IconName)}
                <span
                  className={`truncate ${!showText && 'hidden'} ${isActive || isHovered ? 'text-text-dark' : 'text-sidebar-foreground'} text-md font-normal leading-normal`}
                >
                  {t(item.name)}
                </span>
              </div>
            </Link>
          </div>
          {/* <Ellipsis className="ml-1 shrink-0" /> */}
          {item?.rightIcon && (
            <div
              className={`absolute right-0 top-0 bottom-0 flex items-center pr-1 transition-opacity duration-200 ${isHovered || isActive ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
            >
              {rightIconWithModal(item.rightIcon)}
            </div>
          )}
        </div>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
